import { queryOptions, useQuery } from "@tanstack/react-query";
import { fetchChats } from "@/core/api/chatApi";
import { useAuth } from "@/core/hooks/useAuth";
import { baseQueryOptions } from "@/core/hooks/useApiQueries/baseQueryOptions";
import { useSectorConnection } from "@/core/providers/SectorConnectionProvider/context";
import type { DocID } from "@/types";
import {
  OPEN_CHAT_STATUSES,
  openChatsCountQueryKey,
} from "@/features/chat/utils/openChatsCount";

/**
 * O cache guarda o formato de uma lista infinita SEM linhas (só o total): os
 * leitores de `["chats"]` percorrem `pages[].data` de toda query do prefixo,
 * e assim nenhum deles quebra nem acha conversa aqui.
 */
type OpenChatsCountData = {
  pages: { data: never[]; meta: { pagination: { total: number } } }[];
  pageParams: number[];
};

/**
 * Query da contagem de abertas, para o hook e para o pré-carregamento do boot
 * (SA-1452). Com o recorte "Todas" o usuário não entra no filtro, então o boot
 * monta a mesma request sem o `/user/me`.
 */
export const openChatsCountQueryOptions = ({
  sectorId,
  userId,
  token,
}: {
  sectorId: DocID | null | undefined;
  userId?: DocID;
  token: string | undefined;
}) =>
  queryOptions({
    queryKey: openChatsCountQueryKey(sectorId),
    queryFn: async (): Promise<OpenChatsCountData> => {
      if (!sectorId) throw new Error("Sector ID is required");
      const response = await fetchChats(
        userId ?? "",
        token,
        0,
        sectorId,
        OPEN_CHAT_STATUSES,
        "",
        [],
        undefined,
        1
      );
      return {
        pages: [
          {
            data: [],
            meta: {
              pagination: {
                total: response.meta?.pagination?.total ?? 0,
              },
            },
          },
        ],
        pageParams: [0],
      };
    },
    ...baseQueryOptions,
    // A contagem vem das invalidações (acima), não de remontar: recolher e
    // abrir o menu troca o componente que a lê e não deve refazer a request.
    // Um minuto cobre o buraco de um evento perdido sem pesar no servidor.
    staleTime: 60 * 1000,
    refetchOnMount: true,
  });

/**
 * Total de conversas abertas do setor (Em atendimento + Aguardando), com o
 * mesmo filtro de visibilidade das abas (o servidor aplica) e sem recorte nem
 * tag: é o número do item "Conversas" do menu, que existe em qualquer tela.
 *
 * Custo: UMA request com `pagination[limit]=1&withCount=true` e os dois status
 * no mesmo filtro (o total é a soma), em vez de uma por status. Ela só roda de
 * novo quando `["chats"]` é invalidado ou quando o socket vê uma conversa
 * entrar ou sair desses status; mensagem nova não refaz a contagem.
 *
 * Setor sem WhatsApp não mostra número, mesmo com a contagem em cache (o boot
 * a pré-carrega antes de saber se o setor está conectado).
 */
export function useOpenChatsCount(sectorId?: DocID | null): number | null {
  const { user, token } = useAuth();
  const { connected } = useSectorConnection();

  const query = useQuery({
    ...openChatsCountQueryOptions({
      sectorId,
      userId: user?.documentId,
      token,
    }),
    select: (data) => data.pages[0]?.meta.pagination.total ?? 0,
    enabled: !!user?.documentId && !!sectorId && connected,
  });

  if (!connected) return null;
  return query.data ?? null;
}
